'use client';

import { useEffect, useMemo, type ReactNode } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { NavigationContext } from '@/contexts/NavigationContext';
import type {
  NavigationState,
  ScreenName,
  ScreenParams,
  TabStack,
} from '@/types/navigation';
import {
  buildScreenUrl,
  isKnownScreenPath,
  pathnameToScreen,
  pathnameToTab,
  TAB_URL_MAP,
} from '@/lib/screen-url-map';

/**
 * ─── Per-screen migration checklist (read me!) ────────────────────
 *
 * The legacy reducer-based `useNavigation()` carried a rich object
 * payload via `pushScreen(tab, screen, params)` → params landed on
 * `state.stacks[tab][n].params` and `StackNavigator` passed them to
 * the screen as the `params` prop. Some screens read non-id values
 * out of those params (the full `Match` object, the `ShopItem`,
 * `autoStart: true`, `filter: 'COMPLETED'`).
 *
 * The URL adapter cannot replicate that — only a string-shaped URL
 * can be a target. So when migrating a screen out from under the
 * legacy stack into a Next.js `page.tsx`:
 *
 *  1. Pass *only ids/slugs* through `pushScreen({ id })`. URLs
 *     produced by SCREEN_URL_MAP encode just those.
 *  2. The receiving page.tsx reads ids via `useParams()` and refetches
 *     full payload through React Query. No relying on `props.params`
 *     populated from nav-state.
 *  3. Toggles like `autoStart` go through `?autoStart=1` query and
 *     `useSearchParams()`. Beware: query values are strings, so
 *     `=== true` literals (CaseRouletteScreen.tsx:127) become
 *     `searchParams.get('autoStart') === '1'`.
 *  4. `state.stacks[…][0].params` is intentionally `{}` here. Reading
 *     it from a screen that just migrated is a bug — the lint will
 *     not catch it (ScreenParams is `Record<string, any>`).
 *
 * ──────────────────────────────────────────────────────────────────
 */

type Tab = keyof TabStack;

const PARENT_QUERY_STORAGE_KEY = 'shot:nav-parent-query';

/** Cap on the parent-query memory: one entry per remembered pathname,
 * oldest-evicted-first. 32 covers any plausible drill-down depth and
 * keeps the storage payload tiny — no risk of bumping into Safari's
 * 5MB sessionStorage quota even with long URLs. */
const PARENT_QUERY_LRU_CAP = 32;

/** Stash {pathname → search} for ancestors so a hierarchy back-walk can
 * restore the parent's query string. Session-scoped. */
function rememberParentQueries(pathname: string, search: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(PARENT_QUERY_STORAGE_KEY);
    const map: Record<string, string> = raw ? JSON.parse(raw) : {};
    if (search) {
      // Re-insert at the end (object key order = insertion order in V8) so
      // recent updates push older keys toward the front of the eviction queue.
      delete map[pathname];
      map[pathname] = search;
    } else {
      delete map[pathname];
    }
    const keys = Object.keys(map);
    if (keys.length > PARENT_QUERY_LRU_CAP) {
      for (const k of keys.slice(0, keys.length - PARENT_QUERY_LRU_CAP)) {
        delete map[k];
      }
    }
    sessionStorage.setItem(PARENT_QUERY_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota/parse errors — best-effort */
  }
}

function popRememberedQuery(pathname: string): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = sessionStorage.getItem(PARENT_QUERY_STORAGE_KEY);
    if (!raw) return '';
    const map: Record<string, string> = JSON.parse(raw);
    const search = map[pathname] ?? '';
    if (search) {
      delete map[pathname];
      sessionStorage.setItem(PARENT_QUERY_STORAGE_KEY, JSON.stringify(map));
    }
    return search;
  } catch {
    return '';
  }
}

/** Native browser-history back step. Returns true when navigation was
 * issued; false when there's no prior history entry to step to —
 * caller falls back to the URL-hierarchy walk. */
function goBackNative(router: ReturnType<typeof useRouter>): boolean {
  if (typeof window === 'undefined') return false;
  if (window.history.length <= 1) return false;
  router.back();
  return true;
}

/** "Back" semantics for URL-routed nav. We can't use `router.back()`
 * unconditionally — a deep-link entry starts the session with no
 * in-app history, so `window.history.back()` may no-op. Instead, walk
 * up the URL hierarchy looking for a known screen path, and bottom out
 * at the active tab's root.
 *
 *   /cases/[id]/open   → /cases/[id]   (known)
 *   /cases/[id]        → /cases        (known)
 *   /onboarding/welcome → tabRoot       (parent /onboarding is NOT a real
 *                                        page — there is no /onboarding/page.tsx)
 *   /help/level        → tabRoot       (parent /help is NOT a real page)
 *
 * Search params on the parent are restored from sessionStorage so that
 * `/inventory?filter=COMPLETED → /inventory/[id] → back` lands the user
 * back on the filtered list, not the unfiltered one.
 */
function goBackByHierarchy(
  router: ReturnType<typeof useRouter>,
  pathname: string,
  activeTab: Tab,
): void {
  const targetWithQuery = (parent: string): string => {
    const search = popRememberedQuery(parent);
    return search ? `${parent}?${search}` : parent;
  };

  const tabRoot = TAB_URL_MAP[activeTab];
  if (pathname === tabRoot) {
    router.push('/home');
    return;
  }
  const segments = pathname.split('/').filter(Boolean);
  while (segments.length > 0) {
    segments.pop();
    const parent = segments.length === 0 ? '/' : '/' + segments.join('/');
    if (parent === tabRoot || isKnownScreenPath(parent)) {
      router.push(targetWithQuery(parent));
      return;
    }
  }
  router.push(targetWithQuery(tabRoot));
}

const EMPTY_STACKS: TabStack = {
  balance: [{ screen: 'balance' }],
  cases: [{ screen: 'cases' }],
  games: [{ screen: 'games' }],
  inventory: [{ screen: 'inventory' }],
  referrals: [{ screen: 'referrals' }],
  profile: [{ screen: 'profile' }],
  onboarding: [{ screen: 'onboarding' }],
  shop: [{ screen: 'shop' }],
};

/**
 * URL-backed adapter that shape-matches the legacy `useNavigation()`
 * surface, so the 57+ files that call `navigation.pushScreen(...)` /
 * `navigation.changeTab(...)` work transparently inside Next.js
 * routing once they land under (public) or (auth).
 *
 * Provides the `NavigationContext` exported by `@/contexts/NavigationContext`.
 * Mounted only on the web runtime (`shot.band` host); the TMA shell
 * uses its own `<NavigationProvider>` from the legacy stack-nav inside
 * `runtime/tma/TmaApp`. Web-only: relies on native browser history for
 * back navigation.
 *
 * Notes on the state shape:
 *  - `state.activeTab` is computed from the current pathname (used by
 *    the tab-bar highlight in DesktopHeader and elsewhere).
 *  - `state.stacks[tab]` is a single-entry array reflecting the
 *    current screen at the URL — there is no "stack" in URL-routed
 *    navigation. The entry's `params` is intentionally empty: migrated
 *    screens read URL params via `useParams()` / `useSearchParams()`
 *    directly in their wrapping `page.tsx`. Reading params off the
 *    nav state isn't a supported migration path. Wiring up
 *    `useSearchParams()` here would force every consuming route into
 *    dynamic rendering and break ISR on the marketing `/`.
 *  - `updateCurrentParams` rewrites query string in-place via
 *    `router.replace()`. Param keys with `undefined` values are
 *    deleted, matching the legacy reducer's merge semantics.
 *  - `canGoBack` returns `window.history.length > 1` on the client,
 *    `false` on the server. There is no precise way to know if a
 *    `router.back()` would land within our app vs an external referrer
 *    — leaves should treat this as best-effort.
 */
export function WebUrlNavigationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();

  // Snapshot {pathname → search} on every navigation so that when the
  // user climbs back via goBackByHierarchy the parent's filters/sort
  // are restored.
  useEffect(() => {
    rememberParentQueries(pathname, searchParams?.toString() ?? '');
  }, [pathname, searchParams]);

  const value = useMemo(() => {
    const activeTab: Tab = pathnameToTab(pathname);
    const currentScreen: ScreenName = pathnameToScreen(pathname) ?? activeTab;

    const stacks: TabStack = {
      ...EMPTY_STACKS,
      [activeTab]: [{ screen: currentScreen, params: {} }],
    };

    const state: NavigationState = { activeTab, stacks };

    return {
      state,
      changeTab: (tab: Tab) => {
        router.push(TAB_URL_MAP[tab]);
      },
      pushScreen: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.push(buildScreenUrl(screen, p));
      },
      replaceScreen: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.replace(buildScreenUrl(screen, p));
      },
      popScreen: (_tab: Tab) => {
        if (goBackNative(router)) return;
        goBackByHierarchy(router, pathname, activeTab);
      },
      resetStack: (_tab: Tab, screen: ScreenName, p?: ScreenParams) => {
        router.replace(buildScreenUrl(screen, p));
      },
      goBack: () => {
        if (goBackNative(router)) return;
        goBackByHierarchy(router, pathname, activeTab);
      },
      navigate: (screen: ScreenName, p?: ScreenParams) => {
        router.push(buildScreenUrl(screen, p));
      },
      getCurrentScreen: () => ({ screen: currentScreen, params: {} }),
      // Back is meaningful on every authed page except /home (the
      // app's absolute root) and / (marketing). Defers to native
      // browser history; hierarchy walk is the cold-deep-link fallback.
      canGoBack: () => {
        if (pathname === '/home' || pathname === '/') return false;
        return typeof window !== 'undefined' && window.history.length > 1;
      },
      updateCurrentParams: (next: ScreenParams) => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        for (const [k, v] of Object.entries(next)) {
          if (v === undefined || v === null || v === '') {
            url.searchParams.delete(k);
          } else {
            url.searchParams.set(k, String(v));
          }
        }
        router.replace(url.pathname + url.search + url.hash);
      },
    };
  }, [pathname, router]);

  return (
    <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
  );
}
